import { createSlice } from "@reduxjs/toolkit";
import { PlaylistSearchThunk } from "../thunk/RootThunk";
/**
 * --------------------------------------------------
 * Playlist Search Slice
 * --------------------------------------------------
 * This slice manages:
 * - Searching playlists using tags
 * - Pagination of search results
 * - Like / Unlike playlists from search results
 * - Loading & API status handling
 */

/**
 * State structure for playlist search
 */
export interface SearchState {
	playListSearch: any;
	loading: boolean;
	error: any;
	apiCall: boolean,
	currentPage: Number;
	totalPages: Number;
	totalItems: Number;
	tokenExpiredError: boolean;
}
/**
 * Initial state for playlist search
 */
export const initialState: SearchState = {
	playListSearch: [],
	loading: false,
	error: null,
	apiCall: false,
	currentPage: 0,
	totalPages: 0,
	totalItems: 0,
	tokenExpiredError: false
};

/**
 * Alias export for PlaylistSearchThunk
 * Used for cleaner imports in components
 */
export const playListSearch: any = PlaylistSearchThunk;

const SearchPlaylistSlice = createSlice({
	name: "playListSearch",
	initialState,
	/**
	 * reducers
	 * --------------------------------------------------
	 * Synchronous reducers used for local UI updates
	 * (no API calls here)
	 */
	reducers: {
		// searchPlaylistLike: (state, action) => {
		// 	let all_playlist: any = [...state.playListSearch];
		// 	all_playlist?.map((value, index) => {
		// 		if (action.payload.id === value?._id) {
		// 			if (action.payload?.liked === true) {
		// 				all_playlist[index].liked = false
		// 			}
		// 			if (action.payload?.liked === false) {
		// 				all_playlist[index].liked = true
		// 				all_playlist[index].totalLike = action.payload?.totalLike + 1
		// 			}
		// 			if (action.payload?.totalLike > 0 && action.payload?.liked === true) {
		// 				all_playlist[index].totalLike = action.payload?.totalLike - 1
		// 			}
		// 		}
		// 	})
		// },

		/**
		 * Handles like / unlike action for a playlist
		 * from the search result list.
		 *
		 * This updates UI immediately without refetching
		 * the entire search result.
		 */
		searchPlaylistLike: (state, action) => {
			let all_playlist: any = [...state.playListSearch];
			const playlistIndex = all_playlist?.findIndex(
				(value) => action.payload.id === value?._id
			);
			if (playlistIndex !== -1) {
				const playlistItem = all_playlist[playlistIndex];
				if (action.payload.liked === true) {
					playlistItem.liked = false;
				} else if (action.payload.liked === false) {
					playlistItem.liked = true;
					playlistItem.totalLike = action.payload.totalLike + 1;
				}

				if (action.payload.totalLike > 0 && action.payload.liked === true) {
					playlistItem.totalLike = action.payload.totalLike - 1;
				}
			}
		},
		/**
		 * Manually controls API call flags.
		 * Useful when triggering search from UI
		 * or resetting search state.
		 */
		searchSetApi: (state, action) => {
			// console.log(action.payload);

			state.loading = action.payload;
			state.apiCall = action.payload;
		}

	},
	/**
	 * extraReducers
	 * --------------------------------------------------
	 * Handles lifecycle actions dispatched automatically
	 * by PlaylistSearchThunk.
	 *
	 * Flow:
	 * pending   → API request started
	 * fulfilled → API request successful
	 * rejected  → API request failed
	 */
	extraReducers: (builder) => {
		builder
			/**
			 * Triggered when playlist search API starts
			 * - Show loader
			 * - Reset previous search data
			 */
			.addCase(playListSearch.pending, (state: any, action) => {
				state.loading = true;
				state.apiCall = false;
				state.playListSearch = [];
			})
			/**
			 * Triggered when playlist search API succeeds
			 * - Store playlist data
			 * - Store pagination info
			 */
			.addCase(playListSearch.fulfilled, (state: any, action) => {
				state.loading = false;
				state.apiCall = true;
				if (action.payload.ok === true) {
					state.playListSearch = [...action.payload.data.playlistData];
					state.currentPage = action.payload.data.currentPage;
					state.totalPages = action.payload.data.totalPages;
					state.totalItems = action.payload.data.totalItems;
				}
				if (action.payload.ok === false && action.payload.http$.status === 401) {
					state.tokenExpiredError = true;
				}
			});
	},
});
export const { searchPlaylistLike, searchSetApi } = SearchPlaylistSlice.actions
export default SearchPlaylistSlice.reducer;
